/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact';

type CleanupFuncType = () => void;
export function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void | CleanupFuncType {
  if (ref == null) return;

  if (typeof ref === 'function') {
    return ref(value);
  }

  try {
    ref.current = value;
  } catch (error) {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`);
  }
}

export function mergeRefs<T>(...refs: (Ref<T> | undefined)[]) {
  return (node: T | null) => {
    let cleanupFunc;

    // We need to keep track of cleanup functions from ref callbacks
    // (Preact feature added in PR https://github.com/preactjs/preact/pull/4436).
    // When cleanup functions are detected, create a wrapper around them
    // that will be executed when a component is disconnected from DOM.
    const cleanupFuncArray = refs.reduce<CleanupFuncType[]>((acc, ref) => {
      const cleanupFunc = assignRef(ref, node);
      if (typeof cleanupFunc === 'function') {
        acc.push(cleanupFunc);
      }
      return acc;
    }, []);

    if (cleanupFuncArray.length > 0) {
      cleanupFunc = () => {
        cleanupFuncArray.forEach((func) => func());
      };
    }
    return cleanupFunc;
  };
}
